import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";

import { ApiError } from "@/api/client";
import { changePassword, fetchMe, updateMyPreferences } from "@/api/endpoints";
import { InfoTip } from "@/components/InfoTip";
import { usePageTitle } from "@/hooks/usePageTitle";

const MIN_LENGTH = 12;

function ChangePasswordCard(): JSX.Element {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [pending, setPending] = useState(false);

  const mismatch = confirm.length > 0 && next !== confirm;
  const tooShort = next.length > 0 && next.length < MIN_LENGTH;

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (next !== confirm || next.length < MIN_LENGTH) return;
    setPending(true);
    setError(null);
    setSaved(false);
    try {
      await changePassword(current, next);
      setSaved(true);
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 403
          ? "Current password is incorrect."
          : "Something went wrong — please try again.",
      );
    } finally {
      setPending(false);
    }
  }

  return (
    <section className="card max-w-lg">
      <h2 className="card-title">
        Change password
        <InfoTip
          label="What happens when I change my password?"
          body="Your current session stays signed in. Every other device or browser is signed out and will need the new password."
        />
      </h2>
      <form onSubmit={(e) => void onSubmit(e)} className="space-y-4" noValidate>
        <label className="block space-y-1">
          <span className="text-sm text-ink-200">Current password</span>
          <input
            type="password"
            autoComplete="current-password"
            required
            className="input"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
          />
        </label>
        <label className="block space-y-1">
          <span className="text-sm text-ink-200">New password</span>
          <input
            type="password"
            autoComplete="new-password"
            required
            minLength={MIN_LENGTH}
            className="input"
            value={next}
            onChange={(e) => setNext(e.target.value)}
          />
          {tooShort && (
            <span className="text-xs text-severity-medium">
              At least {MIN_LENGTH} characters
            </span>
          )}
        </label>
        <label className="block space-y-1">
          <span className="text-sm text-ink-200">Confirm new password</span>
          <input
            type="password"
            autoComplete="new-password"
            required
            className="input"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
          {mismatch && (
            <span className="text-xs text-severity-medium">Passwords don&apos;t match</span>
          )}
        </label>

        {error && (
          <p className="text-sm text-severity-high" role="alert">
            {error}
          </p>
        )}
        {saved && (
          <p className="text-sm text-severity-ok-text" role="status">
            Password changed. Other sessions have been signed out.
          </p>
        )}

        <button
          type="submit"
          className="btn-primary"
          disabled={pending || mismatch || tooShort || !current || !next}
        >
          {pending ? "Saving…" : "Change password"}
        </button>
      </form>
    </section>
  );
}

function NotificationsCard({ notifyEmail }: { notifyEmail: boolean }): JSX.Element {
  const queryClient = useQueryClient();
  const toggle = useMutation({
    mutationFn: (next: boolean) => updateMyPreferences({ notify_email: next }),
    onSuccess: (me) => queryClient.setQueryData(["me"], me),
  });

  return (
    <section className="card max-w-lg">
      <h2 className="card-title">Notifications</h2>
      <label className="flex items-start gap-3 text-sm cursor-pointer">
        <input
          type="checkbox"
          className="mt-0.5 accent-current"
          checked={notifyEmail}
          disabled={toggle.isPending}
          onChange={(e) => toggle.mutate(e.target.checked)}
        />
        <span>
          <span className="text-ink-100 block">Monitoring reminder emails</span>
          <span className="text-ink-400">
            A daily digest when tests for your systems are overdue, plus
            heads-up reminders 30, 14, and 7 days before a due date.
          </span>
        </span>
      </label>
      {toggle.isError && (
        <p className="text-sm text-severity-high mt-2" role="alert">
          Couldn&apos;t save — please try again.
        </p>
      )}
    </section>
  );
}

export function Account(): JSX.Element {
  usePageTitle("Your account");
  const me = useQuery({ queryKey: ["me"], queryFn: fetchMe });

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Your account</h1>
      </header>

      <section className="card max-w-lg">
        <h2 className="card-title">Profile</h2>
        {me.isLoading && <div className="skeleton h-16 w-full" />}
        {me.isError && (
          <div>
            <p className="text-sm text-severity-high" role="alert">
              Couldn&apos;t load your profile.
            </p>
            <button
              type="button"
              className="btn-primary text-xs mt-2"
              onClick={() => void me.refetch()}
            >
              Retry
            </button>
          </div>
        )}
        {me.data && (
          <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 text-sm">
            <dt className="text-ink-400">Email</dt>
            <dd className="text-ink-100">{me.data.email}</dd>
            <dt className="text-ink-400">Access</dt>
            <dd className="text-ink-100">
              {me.data.scope}:{me.data.role}
              <InfoTip
                label="Your access level"
                body="Scope is platform (AquaTrack staff — every tenant) or tenant (a single utility). Role is admin (manage users and settings), operator (edit data), or viewer (read-only)."
              />
            </dd>
            <dt className="text-ink-400">Member since</dt>
            <dd className="text-ink-100">
              {new Date(me.data.created_at).toLocaleDateString()}
            </dd>
          </dl>
        )}
      </section>

      {me.data && <NotificationsCard notifyEmail={me.data.notify_email} />}

      <ChangePasswordCard />
    </div>
  );
}
